import React, { useEffect, useRef, useState } from 'react';
import { isLikelyMarkdown, convertMarkdownToHtml } from '../../../utils/markdown.helper';
import { Typography } from 'antd';
import { colorText, secondaryTextColor } from '../../../constants/styles';

const { Text } = Typography;

/**
 * A simple component to render markdown content as HTML.
 * Only supports basic markdown features: headings, bold text, and lists.
 * Uses Ant Design's Text component for paragraphs.
 * 
 * @param {Object} props
 * @param {string} props.content - Markdown content to render
 * @param {Object} props.style - Additional styles to apply to the container
 * @param {string} props.className - Additional CSS classes to apply
 * @param {string} props.type - Text type ('secondary', etc.) similar to Ant Design Text component
 */
const MarkdownRenderer = ({ content, style = {}, className = '', type = '' }) => {
    const containerRef = useRef(null);
    const isMarkdown = content && isLikelyMarkdown(content);
    const [processedHtml, setProcessedHtml] = useState('');
    
    // Generate a unique ID and process HTML when component mounts or content changes
    useEffect(() => {
        if (!isMarkdown) return;
        
        // Convert markdown to HTML
        const html = convertMarkdownToHtml(content);
        
        // Generate a unique ID for this instance
        const uniqueId = `md-renderer-${Math.random().toString(36).substring(2, 10)}`;
        
        // Set the ID to the container element
        if (containerRef.current) {
            containerRef.current.id = uniqueId;
        }
        
        // Process HTML with the unique ID
        const processed = html.replace(/<span class="md-paragraph">/g, 
            `<span class="md-paragraph md-paragraph-${uniqueId}">`);
        
        setProcessedHtml(processed);
        
    }, [isMarkdown, content]);
    
    // Apply styling based on the type
    useEffect(() => {
        // Only proceed if we have markdown content
        if (!isMarkdown || !containerRef.current) return;
        
        const uniqueId = containerRef.current.id;
        
        // Get the appropriate text color based on type
        let textColor = colorText; // default text color
        if (type === 'secondary') {
            textColor = secondaryTextColor; // secondary text color
        }
        
        // Apply custom styling to paragraphs using CSS
        const styleEl = document.createElement('style');
        styleEl.innerHTML = `
            .md-paragraph-${uniqueId} {
                display: block;
                margin-bottom: 12px;
                font-size: 14px;
                line-height: 1.5715;
                color: ${textColor};
            }
            
            /* Apply to all text elements within the container */
            #${uniqueId} {
                color: ${textColor};
            }
            
            /* Ensure headings maintain prominence even with secondary text */
            #${uniqueId} h1, 
            #${uniqueId} h2, 
            #${uniqueId} h3 {
                color: ${type === 'secondary' ? 'rgba(0, 0, 0, 0.65)' : 'rgba(0, 0, 0, 0.85)'};
            }
        `;
        document.head.appendChild(styleEl);
        
        return () => {
            document.head.removeChild(styleEl);
        };
    }, [isMarkdown, type, processedHtml]);
    
    // If no content or not likely markdown, render as plain text
    if (!isMarkdown) {
        return <div className={`markdown-renderer ${className}`} style={style}><Text type={type}>{content}</Text></div>;
    }
    
    return (
        <div 
            ref={containerRef}
            className={`markdown-renderer ${className}`}
            style={style}
            dangerouslySetInnerHTML={{ __html: processedHtml }}
        />
    );
};

export default MarkdownRenderer; 